import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TutorService } from '../tutor.service';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ImageModule } from 'primeng/image';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { Router } from '@angular/router';
import { GlobalFunctions } from '../../../../core/global-functions';
import { CommonService } from '../../../../core/common.service';
import { CONSTANTS } from '../../../../core/constants';

@Component({
  selector: 'app-availability',
  standalone: true,
  imports: [CommonModule, ImageModule, ReactiveFormsModule, FormsModule, MultiSelectModule, DropdownModule, CheckboxModule, ToastModule],
  templateUrl: './availability.component.html',
  styleUrl: './availability.component.scss'
})
export class AvailabilityComponent implements OnInit {

  constants: any = CONSTANTS;

  availabilityForm: FormGroup;
  profileDetails: any;

  isBtnLoading: boolean = false;
  timezones: any[] = [];

  constructor(
    private _formBuilder: FormBuilder,
    private _tutorServices: TutorService,
    private _globalFunctions: GlobalFunctions,
    private messageService: MessageService,
    private _router: Router,
    private _commonService: CommonService
  ) {
    this.populateTimezones();
    this.availabilityForm = this._formBuilder.group({
      timezone: [Intl.DateTimeFormat().resolvedOptions().timeZone, [Validators.required]],
      availabilities: this._formBuilder.array([])
    });
  }

  populateTimezones() {
    const now = new Date();
    const tzNames = (Intl as any).supportedValuesOf('timeZone');
    
    this.timezones = tzNames.map((tz: string) => {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          timeZoneName: 'shortOffset'
        });
        const parts = formatter.formatToParts(now);
        const offset = parts.find(p => p.type === 'timeZoneName')?.value || '';
        return { 
          name: `${tz.replace(/_/g, ' ')} (${offset})`, 
          value: tz 
        };
      } catch (e) {
        return { name: tz, value: tz };
      }
    });

    // Sort: Popular/Local first could be added, but alphabetical with offsets is a good start.
    this.timezones.sort((a, b) => a.name.localeCompare(b.name));
  }

  ngOnInit(): void {
    this.getUserDetails();
  }

  get availabilities(): FormArray {
    return this.availabilityForm.get('availabilities') as FormArray;
  }

  getSlots(dayIndex: number): FormArray {
    return this.availabilities.at(dayIndex).get('slots') as FormArray;
  }

  getUserDetails() {
    this._commonService.getProfile().subscribe({
      next: (result: any) => {
        if (result && result.success && result.data) {
          this.profileDetails = result.data;
          this._commonService.setProfileData(result.data);
          this.prepareAvailabilityFormObj(result.data);
        }
      },
      error: (error: any) => {
        this._globalFunctions.errorHanding(error, this, true);
      }
    });
  }

  prepareAvailabilityFormObj(tempObj: any = {}) {
    const availabilitiesArray = this.availabilities;
    availabilitiesArray.clear();

    const existingAvailabilities = tempObj?.available_days || [];
    const timezone = tempObj?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.availabilityForm.patchValue({ timezone });

    this.constants.days.forEach((day: any) => {
      const existingDay = existingAvailabilities.find((a: any) => a.day_of_week === day.name);
      
      const dayGroup = this._formBuilder.group({
        day_of_week: [day.name],
        selected: [!!existingDay],
        slots: this._formBuilder.array([], this.validateSlots)
      });

      if (existingDay && existingDay.slots && existingDay.slots.length > 0) {
        existingDay.slots.forEach((slot: any) => {
          (dayGroup.get('slots') as FormArray).push(this._formBuilder.group({
            start_time: [slot.start_time?.substring(0, 5), [Validators.required]],
            end_time: [slot.end_time?.substring(0, 5), [Validators.required]]
          }, { validators: this.timeRangeValidator }));
        });
      } else {
        // Add one default slot if not existing, but keep it empty or hidden by 'selected'
        (dayGroup.get('slots') as FormArray).push(this.createSlotGroup());
      }

      availabilitiesArray.push(dayGroup);
    });
  }

  createSlotGroup(start: string = '09:00', end: string = '17:00'): FormGroup {
    return this._formBuilder.group({
      start_time: [start, [Validators.required]],
      end_time: [end, [Validators.required]]
    }, { validators: this.timeRangeValidator });
  }

  addSlot(dayIndex: number) {
    this.getSlots(dayIndex).push(this.createSlotGroup());
  }

  removeSlot(dayIndex: number, slotIndex: number) {
    this.getSlots(dayIndex).removeAt(slotIndex);
  }

  onDayToggle(dayIndex: number) {
    const dayGroup = this.availabilities.at(dayIndex);
    const isSelected = dayGroup.get('selected')?.value;
    if (isSelected && this.getSlots(dayIndex).length === 0) {
      this.addSlot(dayIndex);
    }
  }

  applyToAllDays(sourceDayIndex: number) {
    const sourceSlots = this.getSlots(sourceDayIndex).value;
    if (sourceSlots.length === 0) return;

    this.availabilities.controls.forEach((control: any, index: number) => {
      if (index !== sourceDayIndex) {
        control.patchValue({ selected: true });
        const targetSlotsArray = this.getSlots(index);
        targetSlotsArray.clear();
        sourceSlots.forEach((slot: any) => {
          targetSlotsArray.push(this._formBuilder.group({
            start_time: [slot.start_time, [Validators.required]],
            end_time: [slot.end_time, [Validators.required]]
          }, { validators: this.timeRangeValidator }));
        });
      }
    });
    this.messageService.add({ severity: 'info', summary: 'Copied', detail: 'Schedule applied to all days' });
  }

  clearAll() {
    this.availabilities.controls.forEach((control: any, index: number) => {
      control.patchValue({ selected: false });
      const slots = this.getSlots(index);
      slots.clear();
      slots.push(this.createSlotGroup());
    });
    this.messageService.add({ severity: 'info', summary: 'Reset', detail: 'All selections cleared' });
  }

  quickSelectDays(type: 'weekdays' | 'weekend') {
    const weekdayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const weekendNames = ['Saturday', 'Sunday'];
    const targetNames = type === 'weekdays' ? weekdayNames : weekendNames;

    this.availabilities.controls.forEach((control: any, index: number) => {
      const dayName = control.get('day_of_week')?.value;
      if (targetNames.includes(dayName)) {
        control.patchValue({ selected: true });
        if (this.getSlots(index).length === 0) {
          this.addSlot(index);
        }
      }
    });
    this.messageService.add({ severity: 'success', summary: 'Quick Select', detail: `${type.charAt(0).toUpperCase() + type.slice(1)} selected` });
  }

  timeRangeValidator(group: AbstractControl) {
    const from = group.get('start_time')?.value;
    const to = group.get('end_time')?.value;
    if (from && to && from >= to) {
      return { timeRange: true };
    }
    return null;
  }

  validateSlots(slotsArray: AbstractControl): { [key: string]: any } | null {
    const slots = (slotsArray as FormArray).value;
    if (slots.length < 1) return null;
    
    // Sort slots by start_time for easier overlap check
    const sortedSlots = [...slots].sort((a, b) => a.start_time.localeCompare(b.start_time));
    
    for (let i = 0; i < sortedSlots.length; i++) {
        if (i > 0) {
            if (sortedSlots[i].start_time < sortedSlots[i - 1].end_time) {
                return { overlap: true };
            }
        }
    }
    return null;
  }

  updateAvailability() {
    if (this.availabilityForm.invalid) {
      this.availabilityForm.markAllAsTouched();
      return;
    }

    const formValue = this.availabilityForm.value;
    const selectedAvailabilities = formValue.availabilities
      .filter((day: any) => day.selected)
      .map((day: any) => ({
        day_of_week: day.day_of_week,
        slots: day.slots.map((slot: any) => ({
          start_time: slot.start_time,
          end_time: slot.end_time
        }))
      }));

    if (selectedAvailabilities.length === 0) {
      this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please select at least one day and time slot' });
      return;
    }

    this.isBtnLoading = true;

    const payload = {
      timezone: formValue.timezone,
      availabilities: selectedAvailabilities
    };

    this._tutorServices.updateAvailability(payload).subscribe({
      next: (result: any) => {
        this.isBtnLoading = false;
        if (result && result.success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: result.message });
          this._commonService.getProfile(false, true).subscribe((profileResult: any) => {
            if (profileResult && profileResult.success) {
              this._commonService.setProfileData(profileResult.data);
              this._router.navigate(['/tutor-signup/' + this.constants.tutorRatPerHourUrl]);
            }
          });
        } else {
          this._globalFunctions.successErrorHandling(result, this, true);
        }
      },
      error: (error: any) => {
        this.isBtnLoading = false;
        this._globalFunctions.errorHanding(error, this, true);
      }
    });
  }

  goBack() {
    this._router.navigate(['/tutor-signup/' + this.constants.tutorExperienceUrl]);
  }
}